import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Gift, Trophy, Copy, Share2, Linkedin, Twitter,
  ArrowRight, Star, Award, Zap, Crown, Target, TrendingUp,
  CheckCircle, Loader
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CodeArenaReferralPage = () => {
  const [referralData, setReferralData] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('all');

  const token = localStorage.getItem('codearena_token');

  useEffect(() => {
    if (token) {
      fetchReferralData();
      fetchMyReferrals();
    }
    fetchLeaderboard();
  }, [token, leaderboardPeriod]);

  const fetchReferralData = async () => {
    try {
      const res = await axios.get(`${API}/codearena/me/referral-code`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralData(res.data);
    } catch (err) {
      console.error('Failed to fetch referral data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyReferrals = async () => {
    try {
      const res = await axios.get(`${API}/codearena/me/referrals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferrals(res.data.referrals || []);
    } catch (err) {
      console.error('Failed to fetch referrals:', err);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/codearena/referral-leaderboard?period=${leaderboardPeriod}`);
      setLeaderboard(res.data.leaderboard || []);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    }
  };

  const copyReferralLink = () => {
    if (referralData?.link) {
      navigator.clipboard.writeText(referralData.link);
      toast.success('Referral link copied!');
    }
  };

  const copyReferralCode = () => {
    if (referralData?.code) {
      navigator.clipboard.writeText(referralData.code);
      toast.success('Referral code copied!');
    }
  };

  const shareOnLinkedIn = () => {
    const text = `Join EvalentX Arena - India's premier developer platform! Use my referral code: ${referralData?.code}`;
    const url = referralData?.link || 'https://evalentx.com/codearena';
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(text)}`, '_blank');
  };

  const shareOnTwitter = () => {
    const text = `Join me on @EvalentXArena - India's premier developer platform! Use my referral code: ${referralData?.code}`;
    const url = referralData?.link || 'https://evalentx.com/codearena';
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const rewards = [
    { title: 'Friend Signs Up', points: 100, icon: Users, color: 'cyan' },
    { title: 'Friend Solves First Problem', points: 50, icon: CheckCircle, color: 'emerald' },
    { title: 'Friend Joins Contest', points: 200, icon: Trophy, color: 'purple' },
    { title: 'Company Signs Up', points: 500, icon: Target, color: 'amber' },
    { title: 'Company Posts First Job', points: 1000, icon: Zap, color: 'rose' }
  ];

  const badges = [
    { name: 'Bronze Referrer', requirement: '3+ referrals', icon: Award, color: 'amber' },
    { name: 'Silver Referrer', requirement: '10+ referrals', icon: Star, color: 'gray' },
    { name: 'Gold Referrer', requirement: '25+ referrals', icon: Crown, color: 'yellow' },
    { name: 'Campus Ambassador', requirement: '2+ company referrals', icon: Target, color: 'purple' },
    { name: 'Talent Scout', requirement: '5+ company referrals', icon: Zap, color: 'rose' }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-cyan-500/5 to-transparent" />
        <div className="max-w-6xl mx-auto px-6 py-16 relative">
          <Link to="/codearena" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8">
            ← Back to EvalentX Arena
          </Link>
          
          <div className="text-center">
            <div className="inline-flex p-4 bg-purple-500/20 rounded-2xl mb-6">
              <Gift className="text-purple-400" size={48} />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Refer & <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400">Earn Rewards</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Invite friends and companies to EvalentX Arena. Earn points, unlock badges, and climb the leaderboard!
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-20">
        {/* Referral Code Section */}
        {token ? (
          loading ? (
            <div className="flex justify-center py-12">
              <Loader className="animate-spin text-cyan-400" size={40} />
            </div>
          ) : (
            <div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-purple-500/30 rounded-2xl p-8 mb-12">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Your Referral Info */}
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">Your Referral Dashboard</h2>
                  
                  <div className="bg-white/5 rounded-xl p-4 mb-4">
                    <label className="text-gray-400 text-sm mb-2 block">Your Referral Code</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-2xl font-bold text-cyan-400 tracking-wider">
                        {referralData?.code}
                      </code>
                      <button
                        onClick={copyReferralCode}
                        className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                        data-testid="copy-code-btn"
                      >
                        <Copy size={18} className="text-gray-400" />
                      </button>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 mb-4">
                    <label className="text-gray-400 text-sm mb-2 block">Your Referral Link</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={referralData?.link || ''}
                        readOnly
                        className="flex-1 bg-transparent text-white text-sm truncate"
                      />
                      <button
                        onClick={copyReferralLink}
                        className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                        data-testid="copy-link-btn"
                      >
                        <Copy size={18} className="text-gray-400" />
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={shareOnLinkedIn}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#0077B5]/20 text-[#0077B5] rounded-xl hover:bg-[#0077B5]/30 transition-colors"
                    >
                      <Linkedin size={18} />
                      Share on LinkedIn
                    </button>
                    <button
                      onClick={shareOnTwitter}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#1DA1F2]/20 text-[#1DA1F2] rounded-xl hover:bg-[#1DA1F2]/30 transition-colors"
                    >
                      <Twitter size={18} />
                      Share on Twitter
                    </button>
                  </div>
                </div>

                {/* Stats */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Your Stats</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-purple-400">{referralData?.stats?.total_referrals || 0}</p>
                      <p className="text-gray-400 text-sm">Total Referrals</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-cyan-400">{referralData?.stats?.points_earned || 0}</p>
                      <p className="text-gray-400 text-sm">Points Earned</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-emerald-400">{referralData?.stats?.student_referrals || 0}</p>
                      <p className="text-gray-400 text-sm">Students Referred</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-amber-400">{referralData?.stats?.company_referrals || 0}</p>
                      <p className="text-gray-400 text-sm">Companies Referred</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 mb-12 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Login to Start Referring</h2>
            <p className="text-gray-400 mb-6">Create your account to get your unique referral code and start earning rewards!</p>
            <Link
              to="/codearena/auth"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 text-white rounded-xl font-semibold"
            >
              Login / Register
              <ArrowRight size={18} />
            </Link>
          </div>
        )}

        {/* Rewards Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Earn Points</h2>
          <div className="grid md:grid-cols-5 gap-4">
            {rewards.map((reward, idx) => (
              <div
                key={idx}
                className="bg-white/5 border border-white/10 rounded-xl p-4 text-center hover:border-purple-500/30 transition-colors"
              >
                <div className={`w-12 h-12 bg-${reward.color}-500/20 rounded-xl flex items-center justify-center mx-auto mb-3`}>
                  <reward.icon className={`text-${reward.color}-400`} size={24} />
                </div>
                <p className="text-2xl font-bold text-white mb-1">+{reward.points}</p>
                <p className="text-gray-400 text-xs">{reward.title}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Badges Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Unlock Badges</h2>
          <div className="grid md:grid-cols-5 gap-4">
            {badges.map((badge, idx) => (
              <div
                key={idx}
                className="bg-white/5 border border-white/10 rounded-xl p-4 text-center"
              >
                <div className={`w-12 h-12 bg-${badge.color}-500/20 rounded-xl flex items-center justify-center mx-auto mb-3`}>
                  <badge.icon className={`text-${badge.color}-400`} size={24} />
                </div>
                <p className="text-white font-semibold mb-1">{badge.name}</p>
                <p className="text-gray-500 text-xs">{badge.requirement}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Leaderboard */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">Top Referrers</h2>
            <div className="flex gap-2">
              {['all', 'monthly'].map((period) => (
                <button
                  key={period}
                  onClick={() => setLeaderboardPeriod(period)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    leaderboardPeriod === period
                      ? 'bg-purple-500 text-white'
                      : 'bg-white/5 text-gray-400 hover:text-white'
                  }`}
                >
                  {period === 'all' ? 'All Time' : 'This Month'}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Rank</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Developer</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase">Referrals</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase">Points</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.length > 0 ? (
                  leaderboard.map((entry, idx) => (
                    <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-4 py-3">
                        {entry.rank <= 3 ? (
                          <span className={`text-lg ${
                            entry.rank === 1 ? 'text-amber-400' :
                            entry.rank === 2 ? 'text-gray-300' :
                            'text-orange-400'
                          }`}>
                            {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : '🥉'}
                          </span>
                        ) : (
                          <span className="text-gray-500">#{entry.rank}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-white font-medium">{entry.name}</p>
                        <p className="text-gray-500 text-xs">{entry.college}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-purple-400 font-bold">{entry.referrals}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-cyan-400 font-bold">{entry.points}</span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                      No referrals yet. Be the first to refer!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeArenaReferralPage;
